import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Box, BoxSection } from 'components/_new/Box';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { ArticlePresenter } from 'components/knowledgeBase/ArticlePresenter';
import { Icon } from 'components/_new/Icon';
import { Button } from 'components/_new/Button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProgramPanel } from 'contexts/programPanelContext';
import { ApplicationRole } from 'types/Application';
import { useGlobal } from 'contexts/globalContext';
import { Dropdown } from 'components/_new/Dropdown';
import { Field } from 'components/_new/Field';
import { Skeleton } from 'primereact/skeleton';
import { useScope } from 'contexts/scopeContext';
import { VisitsDetailsDialog } from './components/VisitsDetailsDialog';
import { RemoveArticleDialog } from './components/RemoveArticleDialog';
import { ProgramArticlePresenterDialog } from './components/ProgramArticlePresenterDialog';

type ProgramKnowledgeBaseArticleProps = { disableRating?: boolean };

export const ProgramKnowledgeBaseArticle = ({ disableRating = false }: ProgramKnowledgeBaseArticleProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams({ lng: '' });

	const { toastRef } = useGlobal();
	const { currentProgram: program, organizationName, programName } = useScope();
	const { panelPath } = useProgramPanel();

	const { articleQuery, setArticleLanguage, goToHome, knowledgeBaseLinkPrefix, pathParams, queries, context } =
		useKnowledgeBase();

	const [currentArticleId, setCurrentArticleId] = useState<number | null>(null);
	const [visitsDetailsDialogOpen, setVisitsDetailsDialogOpen] = useState(false);
	const [removeArticleDialogOpen, setRemoveArticleDialogOpen] = useState(false);

	const { data: articleInfo } = useQuery(
		[
			'KNOWLEDGE_BASE_PP_ARTICLE_VISITS',
			{
				programId: program?.id,
				articleId: pathParams?.article?.id,
				context,
			},
		],
		// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
		() => queries.getArticleInfo?.(Number(pathParams?.article?.id))!,
		{ enabled: articleQuery.isFetched }
	);

	const prepareLink = (role: ApplicationRole) => {
		if (articleQuery.isSuccess) {
			return `${process.env.REACT_APP_FRONT_URL}/panel/${organizationName}/${programName}/${role}/knowledge-base/article/${articleQuery.data.articleContent.articleId}`;
		}
		return null;
	};

	const handleAfterRemoveArticle = () => {
		goToHome();
		queryClient.removeQueries(['KNOWLEDGE_BASE_DIRECTORIES']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_SEARCH_LIST']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_TAG_LIST']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_DIRECTORY_LIST']);
	};

	const forceArticleLanguage = searchParams.get('lng');
	useEffect(() => {
		if (forceArticleLanguage && forceArticleLanguage?.length > 0 && articleQuery?.data) {
			const isTargetLanguage = articleQuery.data.articleContent.languageCode === forceArticleLanguage;

			if (!isTargetLanguage) {
				const hasTargetAlternate = articleQuery.data.alternates?.some(
					(alternate) => alternate.language.code === forceArticleLanguage
				);
				if (hasTargetAlternate) {
					searchParams.delete('lng');
					setArticleLanguage(forceArticleLanguage);
				}
			}
			navigate({ search: undefined });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceArticleLanguage, articleQuery.data]);

	const contentParserOptions = {
		handlers: {
			// ...handlers,
			onArticleLinkClick: setCurrentArticleId,
		},
		replaces: {
			panelPrefix: panelPath,
			organization: organizationName,
			program: program?.name,
		},
		disableFormat: true,
	};

	if (articleQuery.isError) {
		return (
			<ErrorContainer
				errorMessage={t('knowledgeBase.errorOccurred')}
				action={() => {
					articleQuery.refetch();
				}}
				error={articleQuery.error}
			/>
		);
	}

	if (articleQuery.isLoading) {
		return (
			<div className="flex flex-column gap-4 sm:flex-row">
				<Box variant="white-bordered" className="flex-1">
					<BoxSection>
						<Skeleton width="60%" height="2.5rem" className="mb-4" />
						<Skeleton width="100%" height="1.5rem" className="mb-2" />
						<Skeleton width="100%" height="1.5rem" className="mb-2" />
						<Skeleton width="100%" height="1.5rem" className="mb-2" />
						<Skeleton width="80%" height="1.5rem" className="mb-4" />
						<Skeleton width="100%" height="200px" />
					</BoxSection>
				</Box>

				<div className="flex flex-column gap-2 w-300-auto">
					<Box variant="white-bordered">
						<BoxSection>
							<Skeleton width="100%" height="2.5rem" className="mb-2" />
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection>
							<Skeleton width="50%" height="1.5rem" className="mb-3" />
							<Skeleton width="100%" height="1.5rem" className="mb-2" />
							<Skeleton width="100%" height="1.5rem" className="mb-3" />
							<Skeleton width="100%" height="2.5rem" />
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection>
							<Skeleton width="60%" height="1.5rem" className="mb-3" />
							<Skeleton width="100%" height="4rem" className="mb-2" />
							<Skeleton width="100%" height="2.5rem" className="mb-3" />
							<Skeleton width="100%" height="4rem" className="mb-2" />
							<Skeleton width="100%" height="2.5rem" />
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection>
							<Skeleton width="40%" height="1.5rem" className="mb-3" />
							<Skeleton width="100%" height="2.5rem" className="mb-2" />
							<Skeleton width="100%" height="2.5rem" />
						</BoxSection>
					</Box>
				</div>
			</div>
		);
	}

	if (articleQuery.isSuccess) {
		if (articleQuery?.data) {
			const keywords =
				articleQuery.data.articleContent.keywords?.map(({ id, text }) => ({ key: String(id), text })) || [];
			return (
				<>
					<ProgramArticlePresenterDialog
						articleId={currentArticleId}
						visible={Boolean(currentArticleId)}
						onHide={() => {
							setCurrentArticleId(null);
						}}
						disableRating
						contentParserOptions={contentParserOptions}
					/>
					{context === 'program-admin' && (
						<VisitsDetailsDialog
							articleId={articleQuery.data.articleContent.articleId}
							visible={visitsDetailsDialogOpen}
							onHide={() => {
								setVisitsDetailsDialogOpen(false);
							}}
						/>
					)}
					<RemoveArticleDialog
						articleId={articleQuery.data.articleContent.articleId}
						visible={removeArticleDialogOpen}
						onHide={() => {
							setRemoveArticleDialogOpen(false);
						}}
						onConfirm={handleAfterRemoveArticle}
					/>
					<div className="flex flex-column gap-4 sm:flex-row">
						<Box variant="white-bordered" className="flex-1">
							<BoxSection header={articleQuery.data.articleContent.title}>
								<ArticlePresenter
									article={articleQuery.data.article}
									articleContent={articleQuery.data.articleContent}
									options={{ disableRating }}
									contentParserOptions={contentParserOptions}
								/>
							</BoxSection>
						</Box>

						<div className="flex flex-column gap-2 w-300-auto">
							<Field label={t('knowledgeBase.programPanel.article.language')} htmlFor="language">
								<Dropdown
									inputId="language"
									value={articleQuery.data.articleContent?.fallbackLanguageCode}
									options={(articleQuery.data.alternates || []).map(
										({ language: { code, nativeName } }) => ({ label: nativeName, value: code })
									)}
									onChange={({ value }) => {
										setArticleLanguage(value);
									}}
								/>
							</Field>

							<Box variant="white-bordered">
								{context === 'program-admin' && (
									<>
										<BoxSection header={t('knowledgeBase.programPanel.article.views')}>
											<div className="flex flex-row gap-2 justify-content-between">
												<div>
													<Icon name="eye" className="text-purple" />
													<span className="ml-2">{t('misc.manyMentees')}</span>
												</div>
												<span className="font-bold">{articleInfo?.visits?.mentee || 0}</span>
											</div>
											<div className="flex flex-row gap-2 justify-content-between">
												<div>
													<Icon name="eye" className="text-purple" />
													<span className="ml-2">{t('misc.manyMentors')}</span>
												</div>
												<span className="font-bold">{articleInfo?.visits?.mentor || 0}</span>
											</div>
										</BoxSection>
										<BoxSection header={t('programPanel.knowledge.ratings')}>
											<div className="flex flex-row gap-2 justify-content-around mb-2">
												<div>
													<Icon
														name="thumbs-up"
														iconStyle="regular"
														className="text-purple"
													/>
													<span className="font-bold ml-2">
														{articleInfo?.ratings?.positive || 0}
													</span>
												</div>
												<div>
													<Icon
														name="thumbs-down"
														iconStyle="regular"
														className="text-purple"
													/>
													<span className="font-bold ml-2">
														{articleInfo?.ratings?.negative || 0}
													</span>
												</div>
											</div>

											<Button
												label={t('programPanel.knowledge.viewAllViews')}
												className="mx-auto"
												onClick={() => {
													setVisitsDetailsDialogOpen(true);
												}}
												disabled={!articleInfo?.visits?.all}
												block
											/>
										</BoxSection>
									</>
								)}
								<BoxSection header={t('knowledgeBase.programPanel.article.keywords')}>
									{keywords.length > 0 ? (
										<div className="flex flex-row flex-wrap gap-2">
											{keywords.map(({ key, text }) => (
												<span
													key={key}
													className="border-1 border-gray-300 bg-gray-100 text-gray-700 border-round px-2 py-0 text-sm"
												>
													{text}
												</span>
											))}
										</div>
									) : (
										<span>{t('misc.empty')}</span>
									)}
								</BoxSection>
							</Box>

							<Box variant="white-bordered">
								<BoxSection header={t('programPanel.knowledge.linksToMaterial')}>
									<div>
										<h3 className="text-base">{t('programPanel.knowledge.forMentees')}:</h3>
										{articleQuery.data.article.menteeAccess ? (
											<>
												<p className="bg-gray-200 text-gray-700 text-wrap-any border-round p-2 text-xs selectable">
													{prepareLink('mentee')}
												</p>
												<Button
													label={t('misc.copyToClipboard')}
													variant="primary-text"
													variantSize="sm"
													block
													icon="copy"
													onClick={() => {
														const text = prepareLink('mentee');
														if (text) {
															navigator.clipboard.writeText(text);
															toastRef?.current?.show({
																severity: 'success',
																life: 3000,
																summary: t('misc.success'),
																detail: t('misc.copiedToClipboard'),
															});
														}
													}}
												/>
											</>
										) : (
											<p>{t('programPanel.knowledge.materialUnavailable')}</p>
										)}
									</div>
									<div>
										<h3 className="text-base">{t('programPanel.knowledge.forMentors')}:</h3>
										{articleQuery.data.article.mentorAccess ? (
											<>
												<p className="bg-gray-200 text-gray-700 text-wrap-any border-round p-2 text-xs selectable">
													{prepareLink('mentor')}
												</p>
												<Button
													label={t('misc.copyToClipboard')}
													variant="primary-text"
													variantSize="sm"
													block
													icon="copy"
													onClick={() => {
														const text = prepareLink('mentor');
														if (text) {
															navigator.clipboard.writeText(text);
															toastRef?.current?.show({
																severity: 'success',
																life: 3000,
																summary: t('misc.success'),
																detail: t('misc.copiedToClipboard'),
															});
														}
													}}
												/>
											</>
										) : (
											<p>{t('programPanel.knowledge.materialUnavailable')}</p>
										)}
									</div>
								</BoxSection>
							</Box>

							<Box variant="white-bordered">
								<BoxSection header={t('programPanel.knowledge.edit')}>
									<div className="flex flex-column gap-2">
										<Button
											label={t('programPanel.knowledge.editMaterial')}
											onClick={() => {
												navigate(
													`${knowledgeBaseLinkPrefix}/edit/${articleQuery.data.articleContent.articleId}`
												);
											}}
											block
											disabled={articleQuery.data.info.isSystem}
											tooltip={
												articleQuery.data.info.isSystem
													? t('programPanel.knowledge.canNotChange')
													: undefined
											}
										/>
										<Button
											label={t('programPanel.knowledge.removeFromKnowledgeBase')}
											onClick={() => {
												setRemoveArticleDialogOpen(true);
											}}
											className="w-full p-"
											variantSize="xs"
											block
											disabled={articleQuery.data.info.isSystem}
											variant="danger"
											tooltip={
												articleQuery.data.info.isSystem
													? t('programPanel.knowledge.canNotChange')
													: undefined
											}
										/>
									</div>
								</BoxSection>
							</Box>
						</div>
					</div>
				</>
			);
		}
	}

	return null;
};
